/**
 * ErrorBoundary — top-level React error boundary for the App tree.
 *
 * Catches panel renders, store subscriptions, and route components.
 * On error: renders a recoverable fallback with reload + reset-state buttons.
 */

import { Component, type ErrorInfo, type ReactNode } from 'react'
import { BrandLogo } from './brand/BrandLogo'

type Props = {
  children: ReactNode
}

type State = {
  error: Error | null
  componentStack: string | null
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error('[xcrmiq] Unhandled error in App tree:', error, info.componentStack)
    this.setState({ componentStack: info.componentStack ?? null })
  }

  private handleReload = () => {
    window.location.reload()
  }

  private handleResetAndReload = () => {
    try {
      window.localStorage.clear()
    } catch {
      // private browsing / storage disabled — best effort
    }
    window.location.reload()
  }

  render() {
    if (!this.state.error) return this.props.children

    return (
      <div className="flex h-screen w-screen items-center justify-center bg-stone-50 p-6">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-lg ring-1 ring-stone-200">
          <BrandLogo variant="full" height={28} className="mb-4" />
          <h1 className="text-lg font-semibold text-stone-800">Something went wrong</h1>
          <p className="mt-2 text-sm text-stone-600">
            xCRMiQ hit an unexpected error. Reloading the page will usually recover. If
            it keeps failing, the "Reset & reload" button clears local state.
          </p>
          <pre className="mt-4 max-h-40 overflow-auto rounded bg-stone-100 p-2 text-xs text-stone-700">
            {this.state.error.message}
          </pre>
          <div className="mt-5 flex justify-end gap-2">
            <button
              onClick={this.handleResetAndReload}
              className="rounded px-3 py-1 text-sm font-medium text-stone-600 hover:bg-stone-100"
            >
              Reset &amp; reload
            </button>
            <button
              onClick={this.handleReload}
              className="rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700"
            >
              Reload
            </button>
          </div>
        </div>
      </div>
    )
  }
}
